/**
 * Dev-only override knobs read from the web URL query string. These let QA
 * trigger flows that would otherwise depend on backend state (e.g. the
 * new-user welcome modal + tooltip tour, which the server only flags while
 * a player has free plays remaining). Native builds (iOS/Android) have no
 * URL query string and always return the inert default.
 *
 * Each flag carries a "forbidden" guard that returns the inert default
 * without touching `window.location`. By default the guard is
 * `NODE_ENV === 'production'`, which lets bundlers tree-shake the URL
 * parsing out of a shipped artifact entirely (the string literal does not
 * survive minification). A flag that must stay live in the production-mode
 * playwright/test web bundle — which is the SAME `expo export:web` artifact
 * as a release build (NODE_ENV=production for both) — passes an explicit
 * guard keyed on the runtime environment instead (see
 * `isShowRotationDebugPanel`).
 *
 * Flags are captured at the first call this page load and cached in a
 * module-level map. PartnerAuth's post-login `navigation.navigate(...)`
 * swaps the URL to `/challenges` and drops the query string, so any
 * downstream caller would otherwise see an empty URL — caching at first
 * sighting bridges that gap. Call `captureUrlOverrides()` from a
 * deterministic load-order spot (an effect early in the auth flow) to
 * prime the cache before navigation rewrites the URL. The cache resets
 * on iframe reload (the SkillstrikeIframePanel re-keys the iframe,
 * triggering a fresh page load that re-executes this module), so
 * toggling the dev knob off and reloading correctly clears the override.
 */

import {smgEnvironmentRaw} from '../constants';

const cache: Record<string, boolean> = {};

function readQueryParam(name: string): string | null {
  if (typeof window === 'undefined' || !window.location?.search) return null;
  return new URLSearchParams(window.location.search).get(name);
}

/** Read + cache a `?<name>=1` URL flag. Assumes the caller's guard has
 *  already decided the flag is permitted in this build. */
function readCachedQueryFlag(name: string): boolean {
  // Use `in` so cached `false` values still count as cached.
  if (name in cache) return cache[name];
  cache[name] = readQueryParam(name) === '1';
  return cache[name];
}

/**
 * Environments where the rotation debug override may be enabled — the
 * developer, CI and internal builds that are never shipped to players. This
 * is an ALLOWLIST: anything not listed (every player-facing deployment —
 * external, external-partner, unlv, partner-prod, prod — the pre-prod
 * staging deployments — stage, staging, staging-partner — and any env added
 * in future) is off by default, so the panel fails closed. Staging is
 * intentionally EXCLUDED (SMG-3880): stage/staging are real deployed
 * environments and must keep the debug UI off, exactly like prod.
 * `dev-partner` is the value the playwright web bundle bakes (APP_ENV=
 * playwright → .env.playwright), which is why the E2E panel still mounts.
 */
const DEBUG_OVERRIDES_ALLOWED_ENVS = new Set([
  'local', 'dev', 'dev-partner', 'internal', 'internal-partner',
  'test',
]);

/**
 * True only in the explicitly non-shipped environments above. The rotation
 * debug panel may respond to its URL flag only when this is true; in every
 * other (deployed/player) environment it stays off regardless of the flag.
 *
 * Fails CLOSED on an empty/unset/whitespace environment. This gate keys off
 * `smgEnvironmentRaw` — the RAW, pre-default env value — NOT the shared
 * `smgEnvironment` export. `smgEnvironment` applies a `|| 'local'` default
 * (constants/index.tsx), and 'local' IS on the allowlist, so a bundle whose
 * build pipeline fails to bake SMG_ENVIRONMENT would arrive at this gate as
 * the allowlisted string 'local' and expose the panel to the URL flag — the
 * default swallows the exact threat this guard exists to catch. Reading the
 * raw value means an unbaked artifact presents an empty/undefined env here
 * and fails closed, while an explicitly-baked env (local, dev-partner, …)
 * still passes.
 */
function isDebugOverridesAllowedEnv(): boolean {
  const env = smgEnvironmentRaw?.trim();
  if (!env) return false;
  return DEBUG_OVERRIDES_ALLOWED_ENVS.has(env);
}

function readFlag(name: string): boolean {
  // The NODE_ENV=production gate is compile-time: bundlers fold it and
  // tree-shake the URL parsing (and this call's flag literal) out of a
  // shipped artifact entirely, so a flag on this path can never be
  // re-enabled in production.
  if (process.env.NODE_ENV === 'production') return false;
  return readCachedQueryFlag(name);
}

/** `?force_new_player=1` — treat the current user as new for UX purposes. */
export function isForceNewPlayer(): boolean {
  return readFlag('force_new_player');
}

/**
 * `?show_rotation_debug=1` — show the rotation turn panel
 * (RoundRobinTurnDisplay). The panel is an internal debugging surface,
 * not product UI: it is hidden for players and only mounts behind this
 * override. Guarded on a runtime environment ALLOWLIST rather than the
 * compile-time NODE_ENV gate `readFlag` uses, because the playwright/test
 * web bundle is the same NODE_ENV=production `expo export:web` artifact as
 * a release build — a NODE_ENV gate would strip the flag from the test
 * bundle too and the rotation seals could never exercise the panel. The
 * allowlist keeps the flag live in dev/test/playwright/internal builds
 * while leaving it off in every deployed/player environment (and any
 * future env) by default.
 */
export function isShowRotationDebugPanel(): boolean {
  if (!isDebugOverridesAllowedEnv()) return false;
  return readCachedQueryFlag('show_rotation_debug');
}

/**
 * Primes the override cache from the current URL. Call this from a
 * deterministic load-order spot (e.g. an early `useEffect` in PartnerAuth)
 * before navigation rewrites the query string. Idempotent — safe to call
 * multiple times.
 */
export function captureUrlOverrides(): void {
  isForceNewPlayer();
  isShowRotationDebugPanel();
}
